/**
 * What the "Search a family or parent code" box on the re-enrolments list matches, and nothing more.
 *
 * The grid's own quick filter looks for the text inside every column, hidden ones included. On the
 * accounts view that is phone numbers, receipt, merchant and biller numbers, settlement dates and
 * every amount — so a parent code such as 8358 also found the family who paid $8,358, the one whose
 * mobile ends in 8358 and the one whose code is 18358. Staff searched for one family, got another
 * on the Paid tab, and read that family's payment as the answer.
 *
 * So the box answers exactly what it says:
 *   - digits alone are a parent code, and only that code matches — whole, not part of a longer one;
 *   - anything else is part of a family name, matched as before.
 *
 * A family who has not finished therefore gives an empty Paid tab, which is the true answer.
 */

const PARENT_CODE = /^\d+$/;

const normalise = (value) => String(value ?? '').trim().toLowerCase();

/** One word of the search against one row. The grid splits the box on spaces and asks per word. */
export const matchesFamilySearch = (word, row = {}) => {
    const term = normalise(word);
    if (!term) return true;

    if (PARENT_CODE.test(term)) {
        return normalise(row.parent_id) === term;
    }
    return normalise(row.family_name).includes(term);
};

/**
 * The same columns with the search narrowed to the family one.
 *
 * Every other column is taken out of the quick filter. The grid treats a column with neither
 * function as not searchable; setting them to undefined overrides the default its type supplies.
 * The column filters on the headings are untouched — they are a different, deliberate question.
 */
export const searchByFamily = (columns, familyField = 'family_name') => columns.map((column) => (
    column.field === familyField
        ? {
            ...column,
            getApplyQuickFilterFn: undefined,
            getApplyQuickFilterFnV7: (word) => (word ? (value, row) => matchesFamilySearch(word, row) : null),
        }
        : { ...column, getApplyQuickFilterFn: undefined, getApplyQuickFilterFnV7: undefined }
));
